if (!customElements.get('product-form')) {
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        if (this.hasChildNodes()) this.init();
      }
  
      init() {
        this.form = this.querySelector('.js-product-form');
        if (this.form) {
          const els = this.form.elements;
          for (let i = 0; i < els.length; i += 1) {
            const el = els[i];
            if (el.name === 'id' && (el.tagName === 'INPUT' || el.tagName === 'SELECT')) {
              el.disabled = false;
            }
          }
          this.submitBtn = this.querySelector('[name="add"]');
  
          if (theme.settings.afterAtc !== 'no-js') {
            this.form.addEventListener('submit', this.handleSubmit.bind(this));
          }
        }
      }

      /** Resolves on read so drawer markup below this section in the layout is found (footer cart drawer). */
      get cartDrawer() {
        return document.querySelector('cart-drawer');
      }
  
      /** `/cart/add` → `/cart/add.js` (JSON Cart API; avoids option-field mismatches). */
      static cartAddJsUrl(route) {
        const base = String(route || '/cart/add').split('?')[0];
        if (base.endsWith('.js')) return base;
        return `${base.replace(/\/$/, '')}.js`;
      }

      /**
       * Reads variant id (prefer #gsVariantId-gs; value used even if temporarily disabled).
       */
      static getVariantIdFromForm(form) {
        const preferredIdEl = form.querySelector('#gsVariantId-gs');
        if (
          preferredIdEl
          && preferredIdEl.name === 'id'
          && preferredIdEl.form === form
          && preferredIdEl.value
        ) {
          return parseInt(String(preferredIdEl.value).trim(), 10);
        }
        for (let i = 0; i < form.elements.length; i += 1) {
          const el = form.elements[i];
          if (
            el.name === 'id'
            && !el.disabled
            && (el.tagName === 'INPUT' || el.tagName === 'SELECT')
            && el.value
          ) {
            const n = parseInt(String(el.value).trim(), 10);
            if (!Number.isNaN(n)) return n;
          }
        }
        return NaN;
      }

      static buildCartAddJsonPayload(form, sectionsStr, sectionsUrl) {
        const variantId = ProductForm.getVariantIdFromForm(form);
        if (Number.isNaN(variantId) || variantId < 1) {
          throw new Error('Missing variant');
        }

        let quantity = 1;
        for (let i = 0; i < form.elements.length; i += 1) {
          const el = form.elements[i];
          if (el.name === 'quantity' && !el.disabled) {
            const q = parseInt(String(el.value).trim(), 10);
            if (!Number.isNaN(q) && q > 0) quantity = q;
            break;
          }
        }

        const item = { id: variantId, quantity };

        for (let i = 0; i < form.elements.length; i += 1) {
          const el = form.elements[i];
          if (el.name === 'selling_plan' && el.value && !el.disabled) {
            const sp = parseInt(String(el.value).trim(), 10);
            if (!Number.isNaN(sp)) item.selling_plan = sp;
            break;
          }
        }

        const propRe = /^properties\[(.+)]$/;
        for (let i = 0; i < form.elements.length; i += 1) {
          const el = form.elements[i];
          if (!el.name || el.disabled) continue;
          const m = propRe.exec(el.name);
          if (!m) continue;
          const t = el.type;
          if ((t === 'checkbox' || t === 'radio') && !el.checked) continue;
          if (!item.properties) item.properties = {};
          item.properties[m[1]] = el.value;
        }

        return {
          items: [item],
          sections: sectionsStr,
          sections_url: sectionsUrl
        };
      }

      /** Themes expect top-level variant_id / quantity like legacy form responses. */
      static normalizeCartAddResponse(raw) {
        if (!raw || typeof raw !== 'object') return raw;
        if (raw.variant_id != null) return raw;
        if (Array.isArray(raw.items) && raw.items.length) {
          const last = raw.items[raw.items.length - 1];
          if (last && last.variant_id != null) {
            return { ...raw, variant_id: last.variant_id, quantity: last.quantity };
          }
        }
        return raw;
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        if (evt.target.id === 'product-signup_form') return;
  
        evt.preventDefault();

        // Theme editor + storefront preview load in an iframe; designMode is not always set.
        const suppressCartNavigation =
          typeof Shopify !== 'undefined' &&
          (Shopify.designMode === true || window.self !== window.top);

        if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;
  
        if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
          window.navigator.vibrate(100);
        }
  
        this.errorMsg = null;
        this.setErrorMsgState();
  
        // Disable "Add to Cart" button until submission is complete.
        this.submitBtn.setAttribute('aria-disabled', 'true');
        this.submitBtn.classList.add('is-loading');
  
        let sections = 'cart-icon-bubble';
        if (this.cartDrawer) {
          const sectionEl = this.cartDrawer.closest('.shopify-section');
          if (sectionEl?.id) {
            sections += `,${sectionEl.id.replace('shopify-section-', '')}`;
          }
        }

        const sectionsUrl = window.location.pathname || '/';
        let payload;
        try {
          payload = ProductForm.buildCartAddJsonPayload(this.form, sections, sectionsUrl);
        } catch (e) {
          this.setErrorMsgState(
            typeof e.message === 'string' ? e.message : 'Could not read variant'
          );
          this.submitBtn.removeAttribute('aria-disabled');
          this.submitBtn.classList.remove('is-loading');
          return;
        }

        const addUrl = ProductForm.cartAddJsUrl(theme.routes.cartAdd);
        const fetchRequestOpts = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(payload)
        };
  
        try {
          const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
          if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
          const oldCartData = await oldCartResponse.json();
  
          const response = await fetch(addUrl, fetchRequestOpts);
          const data = ProductForm.normalizeCartAddResponse(await response.json());
          let error = typeof data.description === 'string' ? data.description : data.message;
          if (data.errors && typeof data.errors === 'object') {
            error = Object.entries(data.errors).map((item) => item[1].join(', '));
          }
  
          if (data.status) this.setErrorMsgState(error);
  
          if (!response.ok) throw new Error(response.status);
  
          if (theme.settings.afterAtc === 'page' && !suppressCartNavigation) {
            // Allow the tick animation to complete
            setTimeout(() => {
              window.location.href = theme.routes.cart;
            }, 300);
          } else {
            // Update cart icon count.
            ProductForm.updateCartIcon(data);
  
            // If item was added from Quick Add drawer, show "Added to cart" message.
            const quickAddDrawer = this.closest('quick-add-drawer');
            if (quickAddDrawer) quickAddDrawer.addedToCart();
  
            setTimeout(() => {
              // Update cart drawer contents.
              if (this.cartDrawer) {
                try {
                  this.cartDrawer.renderContents(
                    data,
                    !quickAddDrawer && theme.settings.afterAtc === 'drawer'
                  );
                } catch (e) {
                  console.error(e); // eslint-disable-line
                }
              } else if (window.location.pathname === theme.routes.cart) {
                const cartItems = document.querySelector('cart-items');
                if (cartItems) {
                  if (cartItems.dataset.empty === 'true' && !suppressCartNavigation) {
                    window.location.reload();
                  } else if (typeof cartItems.refresh === 'function') {
                    cartItems.refresh();
                  }
                }
              }
            }, 700);
          }
  
          const newCartResponse = await fetch(`${theme.routes.cart}.js`);
          if (!newCartResponse.ok) throw new Error(newCartResponse.status);
          const newCartData = await newCartResponse.json();
          const itemInOldCart = oldCartData.items.filter(
            (item) => item.variant_id === data.variant_id
          )[0];
  
          // Check if product was already in the cart
          if (itemInOldCart) {
            this.dispatchEvent(new CustomEvent('on:line-item:change', {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: data.variant_id,
                oldQuantity: itemInOldCart.quantity,
                newQuantity: (itemInOldCart.quantity === data.quantity)
                  ? itemInOldCart.quantity : data.quantity
              }
            }));
          } else {
            this.dispatchEvent(new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: {
                cart: newCartData,
                variantId: data.variant_id
              }
            }));
          }
        } catch (error) {
          console.log(error); // eslint-disable-line
          this.dispatchEvent(new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: {
              error: this.errorMsg.textContent
            }
          }));
        } finally {
          // Re-enable 'Add to Cart' button.
          this.submitBtn.classList.add('is-success');
          this.submitBtn.removeAttribute('aria-disabled');
          setTimeout(() => {
            this.submitBtn.classList.remove('is-loading');
            this.submitBtn.classList.remove('is-success');
          }, 1400);
        }
      }
  
      /**
       * Updates the cart icon count in the header.
       * @param {object} response - Response JSON.
       */
      static updateCartIcon(response) {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        const html = response?.sections?.['cart-icon-bubble'];
        if (cartIconBubble && html != null) {
          cartIconBubble.innerHTML = html;
        }
      }
  
      /**
       * Shows/hides an error message.
       * @param {string} [error=false] - Error to show a message for.
       */
      setErrorMsgState(error = false) {
        this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
        if (!this.errorMsg) return;
  
        this.errorMsg.hidden = !error;
        if (error) {
          this.errorMsg.innerHTML = '';
          const errorArray = Array.isArray(error) ? error : [error];
          errorArray.forEach((err, index) => {
            if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
            this.errorMsg.insertAdjacentText('beforeend', err);
          });
        }
      }
    }
  
    customElements.define('product-form', ProductForm);

    document.addEventListener(
      'submit',
      (evt) => {
        const form = evt.target;
        if (!(form instanceof HTMLFormElement)) return;
        const action = form.getAttribute('action') || '';
        if (!action.includes('/cart/add')) return;
        if (form.classList.contains('js-product-form')) return;

        const suppressNativeCartNav =
          typeof Shopify !== 'undefined' &&
          (Shopify.designMode === true || window.self !== window.top);
        if (suppressNativeCartNav) {
          evt.preventDefault();
        }
      },
      true
    );
  }
  